import { Dispatch, ReactNode, SetStateAction, useEffect } from "react";
import styles from "./DesignTabsWrapper.module.css";
import { Banner, Divider, LegacyCard, Tabs } from "@shopify/polaris";
import { CombinedBundleConfigInterface } from "../../apps/Bundles/Types";
import useBundleMixpanelHook from "../../hooks/useBundleMixpanelHook";
import { BundleTypes } from "../../apps/Bundles/constants";

type Props_DesignTabsWrapper = {
    children: ReactNode;
    activeDesignTab: number;
    setActiveDesignTab: Dispatch<SetStateAction<number>>;
    showFreeGiftSettings?: boolean;
    showUpsellSettings?: boolean;
    isReviewStepEnabled?: boolean;
    hideFooterKeys?: boolean;
    bundleConfig: CombinedBundleConfigInterface;
    submit: any;
    validate: any;
    inspectorTarget: any | null;
};

const DesignTabs = [
    {
        id: "General-1",
        content: "General",
        panelID: "General-1",
        invalidBundles: [BundleTypes.BUNDLE_BUILDER],
    },
    {
        id: "Product Card-1",
        content: "Product Card",
        panelID: "Product Card-1",
        invalidBundles: [],
        validBundles: [],
    },
    {
        id: "Steps Page-1",
        content: "Steps Page",
        panelID: "Steps Page-1",
        invalidBundles: [],
        validBundles: [BundleTypes.BUNDLE_BUILDER, BundleTypes.BB_PLACEHOLDER],
    },

    {
        id: "Review Page-1",
        content: "Review Page",
        panelID: "Review Page-1",
        invalidBundles: [],
        validBundles: [BundleTypes.BUNDLE_BUILDER],
    },
    {
        id: "Free Gift Card-1",
        content: "Free Gift Card",
        panelID: "Free Gift Card-1",
        invalidBundles: [BundleTypes.BUNDLE_BUILDER, BundleTypes.FBT],
        validBundles: [],
    },
    {
        id: "Upsell Products-1",
        content: "Upsell Products",
        panelID: "Upsell Products-1",
        invalidBundles: [],
        validBundles: [BundleTypes.VOLUME_DISCOUNT],
    },
    {
        id: "Footer-1",
        content: "Footer",
        panelID: "Footer-1",
        invalidBundles: [BundleTypes.BUNDLE_BUILDER],
        validBundles: [],
    },
];

export const getBundleTypeLocal: (barType: any) => string = (barType = "") => {
    let obj = "-";
    if (!barType) {
        return obj;
    }
    const type = barType;
    switch (type) {
        case BundleTypes.BXGY:
            obj = "BXGY";
            break;
        case BundleTypes.STANDARD:
            obj = "Standard";
            break;
        case BundleTypes.VOLUME_DISCOUNT:
            obj = "Quantity Break";
            break;
        case BundleTypes.MIX_MATCH:
            obj = "Mix & Match";
            break;
        case BundleTypes.MIX_MATCH_SECTIONAL:
            obj = "Mix & Match (sectional)";
            break;
        case BundleTypes.FBT:
            obj = "FBT";
            break;
        case BundleTypes.PRODUCT_ADDONS:
            obj = "Product Add-on";
            break;
        case BundleTypes.PRODUCT_ADDONS_POPUP:
            obj = "Product Add-on Popup";
            break;
        case BundleTypes.BUNDLE_BUILDER:
            obj = "Bundle Builder";
            break;
        case BundleTypes.BB_PLACEHOLDER:
            obj = "Bundle Builder Placeholder";
            break;
        default:
            obj = "-";
    }
    return obj;
};

const DesignTabsWrapper = ({
    children,
    activeDesignTab,
    setActiveDesignTab,
    showFreeGiftSettings,
    showUpsellSettings,
    isReviewStepEnabled,
    hideFooterKeys,
    bundleConfig,
    submit,
    validate,
    inspectorTarget,
}: Props_DesignTabsWrapper) => {
    const { trackDesignFromTabs } = useBundleMixpanelHook();

    const filterDesignTabs = (tab: any) => {
        if (!showFreeGiftSettings && tab?.id == "Free Gift Card-1") {
            return false;
        }
        if (!showUpsellSettings && tab?.id == "Upsell Products-1") {
            return false;
        }
        if (!isReviewStepEnabled && tab?.id == "Review Page-1") {
            return false;
        }
        if (hideFooterKeys && tab?.id == "Footer-1") {
            return false;
        }
        if (tab?.validBundles?.length > 0) {
            if (tab?.validBundles?.includes?.(bundleConfig?.bundle_type)) {
                return true;
            } else {
                return false;
            }
        } else if (tab?.invalidBundles?.length > 0) {
            if (tab?.invalidBundles?.includes?.(bundleConfig?.bundle_type)) {
                return false;
            } else {
                return true;
            }
        } else {
            return true;
        }
    };
    const TABS = DesignTabs.filter(filterDesignTabs);

    const handleTabChange = (num: number) => {
        submit();
        if (!validate().length) {
            trackDesignFromTabs({
                cta: "Form Tabs Switched",
                tabName: DesignTabs[activeDesignTab].content,
                bundleType: bundleConfig?.bundle_type,
            });
            setActiveDesignTab(num);
        }
    };
    useEffect(() => {
        if (!inspectorTarget) return;

        const index = TABS.findIndex((tab) => tab.id === inspectorTarget?.id);
        if (index >= 0 && index !== activeDesignTab) {
            handleTabChange(index);
        }
    }, [inspectorTarget]);

    return (
        <>
            {![BundleTypes.VOLUME_DISCOUNT, BundleTypes.BUNDLE_BUILDER].includes(bundleConfig?.bundle_type as any) && (
                <div
                    style={{
                        paddingInline: 16,
                    }}
                >
                    <LegacyCard title="">
                        <Banner tone={"warning"}>
                            <p>
                                Any changes made here will be applied to all{" "}
                                {getBundleTypeLocal(bundleConfig?.bundle_type)} bundles.
                            </p>
                        </Banner>
                    </LegacyCard>
                </div>
            )}
            <Tabs tabs={TABS} selected={activeDesignTab} onSelect={handleTabChange}>
                <div
                    style={{
                        paddingInline: 16,
                    }}
                >
                    <LegacyCard title="">
                        <Banner tone={"info"}>
                            <p>Use the Design Editor to customize each element by clicking it in the preview.</p>
                        </Banner>
                    </LegacyCard>
                </div>
                <div className={styles.collapsibleFormBox}>
                    <div style={{ marginTop: "5px", marginBottom: 10 }}>
                        <Divider borderColor="border" />
                    </div>
                    {children}
                </div>
            </Tabs>
        </>
    );
};

export default DesignTabsWrapper;
